// this is a select dropdown component it mui select but it created for using inside react flow canvas because mui select are note scaling in the react flow node while canvas zoom in/out

import React from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useViewport } from '@xyflow/react';
import { fontSize } from '../../..';

export interface CanvasSelectProps<T> {
    value: T | null;
    options: Array<T & { disabled?: boolean }>;
    placeholder?: string;
    emptyLabel?: string;
    getLabel: (value: T) => string;
    getKey: (value: T) => string;
    onChange: (value: T) => void;
    renderOption?: (value: T, selected: boolean) => React.ReactNode;
    onOpen?: () => void;
    disabled?: boolean;
    style?: React.CSSProperties;
    showBorder?: boolean;
}

export function CanvasSelect<T>({
    value,
    options,
    placeholder = 'Select',
    emptyLabel,
    getLabel,
    getKey,
    onChange,
    renderOption,
    onOpen,
    disabled,
    style,
    showBorder = true,
}: CanvasSelectProps<T>) {
    const { zoom } = useViewport();

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selected = options.find(
            (opt) => getKey(opt) === event.target.value
        );
        if (selected) onChange(selected);
    };

    return (
        <FormControl fullWidth size="small">
            <Select
                value={value ? getKey(value) : ''}
                displayEmpty
                disabled={disabled}
                onOpen={onOpen}
                onChange={handleChange}
                IconComponent={ExpandMoreIcon}
                sx={{
                    height: '1.5rem',
                    fontSize: fontSize.medium,
                    textAlign: 'left',
                    ...(showBorder && {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#d3d3d3',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2bb9bb',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2bb9bb',
                        },
                    }),
                    ...style,
                }}
                renderValue={(selected) => {
                    const selectedObj = options.find(
                        (opt) => getKey(opt) === selected
                    );

                    return (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <Typography fontSize={fontSize.medium}>
                                {selectedObj ? getLabel(selectedObj) : placeholder}
                            </Typography>
                        </div>
                    );
                }}
                MenuProps={{
                    slotProps: {
                        paper: {
                            sx: {
                                transform: `scale(${zoom}) !important`
                            }
                        }
                    },
                }}
            >
                {options.length === 0 ? (
                    <MenuItem disabled>
                        <Typography fontSize="12px">
                            {emptyLabel ?? 'No options available'}
                        </Typography>
                    </MenuItem>
                ) : (
                    options.map((opt) => {
                        const selected =
                            value && getKey(opt) === getKey(value);

                        return (
                            <MenuItem
                                key={getKey(opt)}
                                value={getKey(opt)}
                                disabled={opt.disabled}
                            >
                                {renderOption
                                    ? renderOption(opt, !!selected)
                                    : getLabel(opt)}
                            </MenuItem>
                        );
                    })
                )}
            </Select>
        </FormControl>
    );
}